import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import BlogScroll from './BlogScroll';
import {
    BlogPagePreview,
    useBlogScrollSelection,
} from '../Global/DataUtils/blogQueries';
import { getMultiChoice } from '../Global/DataUtils';
import {
    KontentNumberValue,
    KontentRadioMultiChoice,
    KontentStringValue,
} from '../../../types/KontentProps';

type contentDisplayType = 'explicit' | 'relative';
type layoutDisplayType = 'carousel' | 'stacked' | 'gallery';
type sortingOrder = 'most_recent' | 'most_relevant';

export interface BlogScrollProps {
    kontentItemId?: string;
    anchor_name?: KontentStringValue;
    content_display_type: KontentRadioMultiChoice<contentDisplayType>;
    cta_label?: KontentStringValue;
    explicit_content_selection: { value: BlogPagePreview[] };
    featured_blog: { value: BlogPagePreview[] };
    headline?: KontentStringValue;
    layout_display_type?: KontentRadioMultiChoice<layoutDisplayType>;
    relative_large_display?: KontentNumberValue;
    relative_small_display?: KontentNumberValue;
    blog_topic_primary?: KontentRadioMultiChoice<string>;
    blog_topic_secondary?: KontentRadioMultiChoice<string>;
    relative_sorting_order?: KontentRadioMultiChoice<sortingOrder>;
}

const BlogScrollContainer: React.FC<BlogScrollProps> = (props) => {
    const pageMetadataContext = useContext(PageMetadataContext);

    const contentDisplayType = getMultiChoice(props.content_display_type);
    // Relative selections are made at build time by
    // src/utils/blogScrollSelection.js (see gatsby-node.js), so the page only
    // carries the posts it shows instead of the whole blog list.
    const relativeBlogs = useBlogScrollSelection(
        props.kontentItemId,
        contentDisplayType === 'relative'
    );

    const getContent = () => {
        const featuredItem =
            props.featured_blog?.value[0] &&
            props.featured_blog.value[0].elements?.other_metadata?.value[0]
                ?.elements?.blog_scroll_image?.value.length > 0
                ? props.featured_blog.value[0]
                : undefined;

        let selectedContent: BlogPagePreview[] = [];

        switch (contentDisplayType) {
            case 'explicit':
                selectedContent = props.explicit_content_selection.value.filter(
                    (selectedContentItem) =>
                        selectedContentItem &&
                        selectedContentItem.elements?.other_metadata?.value[0]
                            ?.elements?.blog_scroll_image?.value.length > 0
                );
                break;
            case 'relative':
                selectedContent = relativeBlogs;
                break;
            default:
                break;
        }

        if (featuredItem) {
            return [featuredItem, ...selectedContent];
        }

        return selectedContent;
    };

    const content = getContent();
    return (
        <BlogScroll
            {...props}
            content={content}
            preferredLanguage={pageMetadataContext.preferredLanguage}
        />
    );
};

export default BlogScrollContainer;

export const fragmentBlogScroll = graphql`
    fragment BlogScroll on kontent_item_component___blog_scroll {
        elements {
            anchor_name {
                value
            }
            blog_topic_primary {
                value {
                    codename
                }
            }
            blog_topic_secondary {
                value {
                    codename
                }
            }
            content_display_type {
                value {
                    codename
                }
            }
            cta_label {
                value
            }
            headline {
                value
            }
            layout_display_type {
                value {
                    codename
                }
            }
            relative_large_display {
                value
            }
            relative_small_display {
                value
            }
            relative_sorting_order {
                value {
                    codename
                }
            }
            explicit_content_selection {
                value {
                    ...blogPagePreview
                }
            }
            featured_blog {
                value {
                    ...blogPagePreview
                }
            }
        }
    }
`;
